import React from 'react';
import {
    FiChevronLeft,
    FiChevronRight,
    FiChevronsLeft,
    FiChevronsRight,
} from 'react-icons/fi';

interface PaginationData {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

interface PaginationProps {
    pagination: PaginationData;
    onPageChange: (page: number) => void;
    onLimitChange: (limit: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
    pagination,
    onPageChange,
    onLimitChange,
}) => {
    const { current_page, total_pages, total, per_page, has_next_page, has_prev_page } = pagination;
    const rows_options = [5, 10, 20, 50];

    const getPageNumbers = () => {
        // Ensure total_pages is at least 1
        const actualTotalPages = Math.max(total_pages, 1);
        const max_pages_to_show = 5;
        let start_page = Math.max(current_page - 2, 1);
        let end_page = Math.min(start_page + max_pages_to_show - 1, actualTotalPages);

        if (end_page - start_page < max_pages_to_show - 1 && start_page > 1) {
            start_page = Math.max(end_page - max_pages_to_show + 1, 1);
        }

        const pages: number[] = [];
        for (let i = start_page; i <= end_page; i++) {
            pages.push(i);
        }
        return pages;
    };

    // Calculate the range of items being displayed
    const startItem = total === 0 ? 0 : (current_page - 1) * per_page + 1;
    const endItem = Math.min(current_page * per_page, total);

    return (
        <div className="flex flex-col w-full lg:flex-row items-center justify-between p-4 bg-gray-100 rounded-lg shadow-md">
            <div className="flex items-center space-x-3 mb-4 lg:mb-0">
                <span className="text-sm text-gray-600 font-medium">Rows per page:</span>
                <select
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 transition-all"
                    value={per_page}
                    onChange={(e) => {
                        const newLimit = Number(e.target.value);
                        onLimitChange(newLimit);
                    }}
                >
                    {rows_options.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                <span className="text-sm text-gray-600">
                    {total > 0
                        ? `Showing ${startItem}-${endItem} of ${total} items`
                        : 'No items to display'
                    }
                </span>
            </div>

            <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 mr-2">
                    Page {current_page} of {Math.max(total_pages, 1)}
                </span>

                <button
                    type="button"
                    onClick={() => onPageChange(1)}
                    disabled={!has_prev_page || total_pages <= 1}
                    className="p-2 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
                    aria-label="First page"
                >
                    <FiChevronsLeft className="w-5 h-5" />
                </button>

                <button
                    type="button"
                    onClick={() => onPageChange(current_page - 1)}
                    disabled={!has_prev_page || total_pages <= 1}
                    className="p-2 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
                    aria-label="Previous page"
                >
                    <FiChevronLeft className="w-5 h-5" />
                </button>

                {getPageNumbers().map((page) => (
                    <button
                        type="button"
                        key={page}
                        onClick={() => onPageChange(page)}
                        className={`px-3 py-2 rounded-md text-sm font-semibold transition-all ${page === current_page
                            ? 'bg-primary-700 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        aria-label={`Page ${page}`}
                        aria-current={page === current_page ? 'page' : undefined}
                    >
                        {page}
                    </button>
                ))}

                <button
                    type="button"
                    onClick={() => onPageChange(current_page + 1)}
                    disabled={!has_next_page || total_pages <= 1}
                    className="p-2 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
                    aria-label="Next page"
                >
                    <FiChevronRight className="w-5 h-5" />
                </button>

                <button
                    type="button"
                    onClick={() => onPageChange(total_pages)}
                    disabled={!has_next_page || total_pages <= 1}
                    className="p-2 rounded disabled:opacity-50 hover:bg-gray-200 transition-colors"
                    aria-label="Last page"
                >
                    <FiChevronsRight className="w-5 h-5" />
                </button>
            </div>
        </div>
    );
};

export default Pagination;